import { useCancelOrderDialog } from '@/components/OrdersModule/OrderDetail/CancelOrderDialog/hooks/useCancelOrderDialog'
import CustomAcceptedDeliveryTimeAdjuster from '@/components/OrdersModule/OrderDetail/Footer/AcceptedDeliveryTimeSelector/CustomAcceptedDeliveryTimeAdjuster'
import DefaultAcceptedDeliveryTimeSelector from '@/components/OrdersModule/OrderDetail/Footer/AcceptedDeliveryTimeSelector/DefaultAcceptedDeliveryTimeSelector'
import TerminalAPI from '@/features/api/terminal/terminal'
import { logger } from '@/features/orders/logger'
import { DeliveryTime } from '@/features/orders/types/app'
import { convertDeliveryTimeTypeToFirebase } from '@/features/orders/utils/conversions'
import { useAppSelector } from '@/hooks/redux'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { ordersSelectors } from '@/redux/reducers/orders/selectors'
import { RequestedDeliveryTimeShiftValues } from '@/types/order-flow-action-time-values'
import { shiftDeliveryTime } from '@/utils/customDeliveryTime'
import { FC, useState } from 'react'
import invariant from 'tiny-invariant'

interface AcceptedDeliveryTimeSelectorProps {
    orderUuid: string
}

const AcceptedDeliveryTimeSelector: FC<AcceptedDeliveryTimeSelectorProps> = ({ orderUuid }) => {
    const order = useAppSelector(ordersSelectors.orderByUuid(orderUuid))
    const showCancelOrderDialog = useCancelOrderDialog(orderUuid)

    const [shiftValue, setShiftValue] = useState<RequestedDeliveryTimeShiftValues>(0)
    const [isCustomValueMode, setIsCustomValueMode] = useState(false)
    const [customDeliveryTime, setCustomDeliveryTime] = useState<DeliveryTime>()

    const handleError = useHandleErrorWithToast(logger)

    if (!(order && order.requestedDeliveryTime)) {
        return null
    }

    const handleDeliveryTimeShiftPress = (value: RequestedDeliveryTimeShiftValues) => {
        if (value === 'custom') {
            setIsCustomValueMode(true)
            return
        }
        setShiftValue(value)
    }

    const handleCustomTimeShiftPress = (deliveryTime: DeliveryTime) => {
        setCustomDeliveryTime(deliveryTime)
    }

    const handleCancelCustomDeliveryTime = () => {
        setCustomDeliveryTime(undefined)
        setIsCustomValueMode(false)

        if (shiftValue !== 'custom') return

        setShiftValue(0)
    }

    const handleConfirmCustomDeliveryTime = () => {
        setShiftValue('custom')
        setIsCustomValueMode(false)
    }

    const handleOrderAcceptButton = async () => {
        try {
            if (customDeliveryTime && shiftValue === 'custom') {
                await TerminalAPI.acceptOrder(order.id, {
                    ...customDeliveryTime,
                    type: convertDeliveryTimeTypeToFirebase(customDeliveryTime.type),
                })
                return
            }

            invariant(
                order.requestedDeliveryTime && typeof shiftValue === 'number',
                'Invalid input: requestedDeliveryTime is missing or shiftValue is not a number'
            )

            const deliveryTime = shiftDeliveryTime(
                order.requestedDeliveryTime,
                shiftValue,
                'minutes'
            )
            await TerminalAPI.acceptOrder(order.id, {
                ...deliveryTime,
                type: convertDeliveryTimeTypeToFirebase(deliveryTime.type),
            })
        } catch (e) {
            handleError(e)
        }
    }

    return (
        <>
            {!isCustomValueMode ? (
                <DefaultAcceptedDeliveryTimeSelector
                    requestedDeliveryTime={order.requestedDeliveryTime}
                    customDeliveryTime={customDeliveryTime}
                    shiftValue={shiftValue}
                    onDeliveryTimeShiftPress={handleDeliveryTimeShiftPress}
                    onOrderCancelButton={showCancelOrderDialog}
                    onOrderAcceptButton={handleOrderAcceptButton}
                />
            ) : (
                <CustomAcceptedDeliveryTimeAdjuster
                    requestedDeliveryTime={order.requestedDeliveryTime}
                    customDeliveryTime={customDeliveryTime}
                    onTimeShiftPress={handleCustomTimeShiftPress}
                    onCancelButton={handleCancelCustomDeliveryTime}
                    onConfirmButton={handleConfirmCustomDeliveryTime}
                />
            )}
        </>
    )
}

export default AcceptedDeliveryTimeSelector
